import React, { useEffect, useState } from 'react';
import {
  Shield, Plus, Trash2, KeyRound, Pencil, X, Save, AlertCircle,
  CheckCircle2, Mail, Crown, Copy, Check, BellOff,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

const ROLES = [
  { value: 'admin', label: 'Super-administrador (acceso total)', color: 'rose' },
  { value: 'council', label: 'Miembro del consejo (permisos granulares)', color: 'cmic' },
  { value: 'asistente', label: 'Asistente (permisos granulares, fuera del consejo)', color: 'indigo' },
];

const FORUM_MOD_OPTIONS = [
  { key: 'moderate_thread', label: 'Fijar y cerrar hilos', desc: 'Marcar hilos como fijados o cerrarlos a nuevas respuestas.' },
  { key: 'edit_post', label: 'Editar publicaciones de otros', desc: 'Corregir o limpiar mensajes de otros miembros (groserías, etc.).' },
  { key: 'delete_post', label: 'Eliminar publicaciones de otros', desc: 'Borrar respuestas de otros miembros.' },
  { key: 'delete_thread', label: 'Eliminar hilos de otros', desc: 'Borrar temas completos de otros miembros.' },
  { key: 'create_category', label: 'Crear y borrar categorías', desc: 'Administrar las secciones del foro.' },
  { key: 'block_user', label: 'Bloquear usuarios del foro', desc: 'Bloquear a un miembro de forma temporal (días/meses/años) o permanente.' },
];
const ALL_FORUM_MOD = FORUM_MOD_OPTIONS.map((o) => o.key);

const emptyForm = {
  username: '',
  full_name: '',
  email: '',
  role: 'council',
  permissions: [],
  council_title: '',
  is_active: true,
  is_council: false,
  notify_approvals: true,
  forum_mod_permissions: [...ALL_FORUM_MOD],
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [permGroups, setPermGroups] = useState([]);
  const [permActions, setPermActions] = useState([]);
  const [boardRoles, setBoardRoles] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // 'new' | userId | null
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/users/permissions'),
      ]);
      setUsers(u.data || []);
      setPermGroups(p.data.groups || []);
      setPermActions(p.data.actions || []);
      // Council titles come from the "Consejo Directivo" board roles.
      try {
        const b = await api.get('/board-members');
        const roles = Array.from(new Set((b.data || []).map((m) => (m.role || '').trim()).filter(Boolean)));
        setBoardRoles(roles);
      } catch { /* board roles optional */ }
      try {
        const sr = await api.get('/admin/staff-roles');
        setCustomRoles(sr.data || []);
      } catch { /* custom roles optional */ }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openNew() {
    setEditing('new');
    setForm({ ...emptyForm });
    setError('');
  }

  function openEdit(u) {
    setEditing(u.id);
    setForm({
      username: u.username,
      full_name: u.full_name || '',
      email: u.email || '',
      role: u.role,
      permissions: u.permissions || [],
      council_title: u.council_title || '',
      is_active: u.is_active !== false,
      is_council: u.is_council === true,
      notify_approvals: u.notify_approvals !== false,
      forum_mod_permissions: Array.isArray(u.forum_mod_permissions) ? u.forum_mod_permissions : [...ALL_FORUM_MOD],
    });
    setError('');
  }

  function close() {
    setEditing(null);
    setForm(emptyForm);
    setError('');
  }

  function hasGrant(group, action) {
    const arr = form.permissions || [];
    if (arr.includes(group)) return true;             // legacy: full group
    if (arr.includes(`${group}:${action}`)) return true;
    return false;
  }

  function toggleAction(group, action) {
    const arr = form.permissions || [];
    const granular = `${group}:${action}`;
    // If the legacy "full group" perm is set, expand it into granular grants
    // first so that turning OFF one action removes only that one.
    let next = arr;
    if (arr.includes(group)) {
      next = arr.filter((p) => p !== group);
      const groupDef = permGroups.find((g) => g.key === group);
      (groupDef?.actions || []).forEach((a) => {
        if (a !== action) next = [...next, `${group}:${a}`];
      });
    } else if (arr.includes(granular)) {
      next = arr.filter((p) => p !== granular);
    } else {
      next = [...arr, granular];
    }
    setForm({ ...form, permissions: next });
  }

  function setGroupAllOrNone(group, on) {
    const arr = form.permissions || [];
    // Remove all existing grants for this group (legacy + granular)
    const cleaned = arr.filter((p) => p !== group && !p.startsWith(`${group}:`));
    if (!on) {
      setForm({ ...form, permissions: cleaned });
    } else {
      const groupDef = permGroups.find((g) => g.key === group);
      const next = [...cleaned, ...(groupDef?.actions || []).map((a) => `${group}:${a}`)];
      setForm({ ...form, permissions: next });
    }
  }

  async function save() {
    setSaving(true);
    setError('');
    try {
      const payload = {
        username: form.username.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        role: form.role,
        permissions: form.role === 'admin' ? [] : form.permissions,
        council_title: form.role === 'council' ? (form.council_title || '') : '',
        is_active: form.is_active,
        is_council: form.role === 'admin' ? false : !!form.is_council,
        notify_approvals: !!form.notify_approvals,
        forum_mod_permissions: form.role === 'admin' ? null : (form.forum_mod_permissions || []),
      };
      if (editing === 'new') {
        const { data } = await api.post('/admin/users', payload);
        setFeedback({
          type: 'success',
          msg: `Usuario "${data.user.username}" creado. ${data.email_sent
            ? 'Recibirá un correo con su contraseña temporal (en modo prueba va a la casilla de pruebas).'
            : '⚠️ El correo de invitación NO se envió: ' + (data.email_message || 'error desconocido') + '. Genera una nueva contraseña manualmente desde el botón de la fila.'}`,
        });
      } else {
        await api.put(`/admin/users/${editing}`, payload);
        setFeedback({ type: 'success', msg: 'Cambios guardados.' });
      }
      await load();
      close();
      setTimeout(() => setFeedback(null), 8000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  }

  async function remove(u) {
    if (!window.confirm(`¿Eliminar al usuario "${u.username}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      await load();
      setFeedback({ type: 'success', msg: 'Usuario eliminado.' });
      setTimeout(() => setFeedback(null), 5000);
    } catch (e) {
      setFeedback({ type: 'error', msg: formatApiError(e.response?.data?.detail) || 'No se pudo eliminar.' });
      setTimeout(() => setFeedback(null), 8000);
    }
  }

  async function resetPassword(u) {
    if (!window.confirm(`¿Regenerar la contraseña de "${u.username}"? Le llegará por correo (o a la casilla de pruebas si está activado modo prueba).`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setFeedback({
        type: 'success',
        msg: data.email_sent
          ? `Nueva contraseña enviada por correo a ${u.email}.`
          : `Contraseña regenerada pero el correo NO se envió: ${data.email_message || 'error'}.`,
      });
      setTimeout(() => setFeedback(null), 8000);
    } catch (e) {
      setFeedback({ type: 'error', msg: formatApiError(e.response?.data?.detail) || 'Error al resetear.' });
      setTimeout(() => setFeedback(null), 8000);
    }
  }

  async function setManualPassword(password, force) {
    const { data } = await api.post(`/admin/users/${editing}/set-password`, {
      new_password: password,
      force_password_change: force,
    });
    setFeedback({
      type: 'success',
      msg: data.must_change_password
        ? 'Contraseña establecida. El usuario deberá cambiarla en su próximo inicio de sesión.'
        : 'Contraseña establecida. El usuario ya puede iniciar sesión con ella.',
    });
    await load();
    setTimeout(() => setFeedback(null), 8000);
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-900 flex items-center gap-2">
            <Shield size={22} className="text-cmic-600" />
            Usuarios del panel admin
          </h1>
          <p className="text-sm text-charcoal-500 mt-1 max-w-2xl">
            Gestiona quién tiene acceso al panel. Los <strong>miembros del consejo</strong> reciben permisos granulares
            por área. El <strong>super-administrador</strong> es el único que puede crear, modificar o eliminar usuarios
            aquí, y siempre debe haber al menos uno.
          </p>
        </div>
        <button
          onClick={openNew}
          data-testid="admin-users-new-btn"
          className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cmic-700"
        >
          <Plus size={16} />Nuevo usuario
        </button>
      </div>

      {feedback && (
        <div
          className={`flex items-start gap-2 rounded-lg p-3 ${feedback.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'}`}
          data-testid="admin-users-feedback"
        >
          {feedback.type === 'success' ? <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" /> : <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />}
          <p className="text-sm">{feedback.msg}</p>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-sm text-charcoal-400 text-center py-12">Cargando usuarios…</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="admin-users-table">
          {users.map((u) => (
            <div key={u.id} className="border-b border-gray-100 last:border-b-0 p-4 flex flex-wrap items-center gap-3 hover:bg-gray-50">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cmic-500 to-cmic-700 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                {u.full_name ? u.full_name.split(' ').slice(0, 2).map((s) => s.charAt(0)).join('').toUpperCase() : u.username.slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-charcoal-800 truncate">{u.full_name || u.username}</span>
                  <span className="text-xs text-charcoal-400">@{u.username}</span>
                  {u.role === 'admin' ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full border border-rose-200">
                      <Crown size={10} />Super-admin
                    </span>
                  ) : u.role === 'council' ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-cmic-50 text-cmic-700 px-2 py-0.5 rounded-full border border-cmic-200">
                      Consejo
                    </span>
                  ) : u.role === 'asistente' ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full border border-indigo-200">
                      Asistente
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full border border-slate-300" data-testid={`admin-users-customrole-${u.id}`}>
                      {customRoles.find((r) => r.key === u.role)?.name || u.role}
                    </span>
                  )}
                  {u.is_council && u.role !== 'admin' && u.role !== 'council' && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-cmic-50 text-cmic-700 px-2 py-0.5 rounded-full border border-cmic-200" data-testid={`admin-users-consejo-flag-${u.id}`}>
                      Consejo
                    </span>
                  )}
                  {u.role === 'council' && u.council_title && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full border border-indigo-200" data-testid={`admin-users-title-${u.id}`}>
                      {u.council_title}
                    </span>
                  )}
                  {!u.is_active && (
                    <span className="text-[10px] font-medium uppercase tracking-wider bg-gray-100 text-charcoal-500 px-2 py-0.5 rounded-full">
                      Inactivo
                    </span>
                  )}
                  {u.notify_approvals === false && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200" title="No recibe correos de solicitudes de cambio de perfil (los revisa en el panel)" data-testid={`admin-users-nonotify-${u.id}`}>
                      <BellOff size={10} />Sin correos
                    </span>
                  )}
                  {u.must_change_password && (
                    <span className="text-[10px] font-medium uppercase tracking-wider bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200" title="Aún no ha cambiado la contraseña temporal">
                      Pendiente cambiar password
                    </span>
                  )}
                </div>
                <p className="text-xs text-charcoal-500 truncate mt-0.5 flex items-center gap-1">
                  <Mail size={11} />{u.email || 'sin email'}
                </p>
                {(u.role === 'council' || u.role === 'asistente') && u.permissions?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`admin-users-perms-${u.id}`}>
                    {(() => {
                      // Aggregate granular perms by group → action list, render compact chips.
                      const byGroup = {};
                      const actionLabels = Object.fromEntries(permActions.map((a) => [a.key, a.label]));
                      (u.permissions || []).forEach((p) => {
                        if (p.includes(':')) {
                          const [g, a] = p.split(':');
                          (byGroup[g] = byGroup[g] || []).push(actionLabels[a] || a);
                        } else {
                          byGroup[p] = ['Todo'];
                        }
                      });
                      return Object.entries(byGroup).map(([g, acts]) => (
                        <span key={g} className="text-[10px] bg-gray-100 text-charcoal-600 px-2 py-0.5 rounded">
                          <strong>{permGroups.find((pg) => pg.key === g)?.label || g}:</strong> {acts.join(', ')}
                        </span>
                      ));
                    })()}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => resetPassword(u)}
                  data-testid={`admin-users-reset-${u.id}`}
                  className="p-2 text-charcoal-500 hover:text-cmic-600 hover:bg-cmic-50 rounded-lg"
                  title="Restablecer contraseña (envía correo)"
                >
                  <KeyRound size={15} />
                </button>
                <button
                  onClick={() => openEdit(u)}
                  data-testid={`admin-users-edit-${u.id}`}
                  className="p-2 text-charcoal-500 hover:text-cmic-600 hover:bg-cmic-50 rounded-lg"
                  title="Editar"
                >
                  <Pencil size={15} />
                </button>
                <button
                  onClick={() => remove(u)}
                  data-testid={`admin-users-delete-${u.id}`}
                  className="p-2 text-charcoal-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg"
                  title="Eliminar"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editing !== null && (
        <UserFormModal
          isNew={editing === 'new'}
          form={form}
          setForm={setForm}
          permGroups={permGroups}
          permActions={permActions}
          boardRoles={boardRoles}
          customRoles={customRoles}
          hasGrant={hasGrant}
          toggleAction={toggleAction}
          setGroupAllOrNone={setGroupAllOrNone}
          onClose={close}
          onSave={save}
          onSetPassword={setManualPassword}
          saving={saving}
          error={error}
        />
      )}
    </div>
  );
}


function UserFormModal({ isNew, form, setForm, permGroups, permActions, boardRoles, customRoles, hasGrant, toggleAction, setGroupAllOrNone, onClose, onSave, onSetPassword, saving, error }) {
  const [pw, setPw] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [forcePw, setForcePw] = useState(true);
  const [savingPw, setSavingPw] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSet, setPwSet] = useState('');
  const [copied, setCopied] = useState(false);
  const pwMismatch = pw && pwConfirm && pw !== pwConfirm;
  const pwTooShort = pw && pw.length < 8;
  const canSetPw = pw && pwConfirm && !pwMismatch && !pwTooShort && !savingPw;

  async function handleSetPassword() {
    setPwError('');
    if (pwTooShort) { setPwError('Mínimo 8 caracteres.'); return; }
    if (pwMismatch) { setPwError('Las contraseñas no coinciden.'); return; }
    setSavingPw(true);
    try {
      await onSetPassword(pw, forcePw);
      setPwSet(pw);
      setPw('');
      setPwConfirm('');
    } catch (e) {
      setPwError(formatApiError(e.response?.data?.detail) || 'No se pudo establecer la contraseña.');
    } finally {
      setSavingPw(false);
    }
  }

  async function copyPassword() {
    try {
      await navigator.clipboard.writeText(pwSet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setPwError('No se pudo copiar automáticamente. Cópiala manualmente.');
    }
  }

  const roleOptions = [
    ...ROLES,
    ...(customRoles || []).map((r) => ({ value: r.key, label: `${r.name} (rol personalizado)`, color: 'slate', custom: true })),
  ];
  const isCustomRole = !['admin', 'council', 'asistente'].includes(form.role);
  const activeCustomRole = (customRoles || []).find((r) => r.key === form.role);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="admin-users-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="font-semibold text-charcoal-800">
            {isNew ? 'Nuevo usuario admin / consejo' : `Editar: @${form.username}`}
          </h3>
          <button onClick={onClose} className="p-1 text-charcoal-500 hover:text-charcoal-800" data-testid="admin-users-modal-close">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          {error && (
            <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-lg" data-testid="admin-users-modal-error">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-charcoal-600 mb-1">Usuario *</label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                disabled={!isNew}
                data-testid="admin-users-username"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono disabled:opacity-50 disabled:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-cmic-500/30"
                placeholder="ej. m.gonzalez"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-charcoal-600 mb-1">Nombre completo</label>
              <input
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                data-testid="admin-users-fullname"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30"
                placeholder="María González"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-charcoal-600 mb-1">Correo electrónico *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="admin-users-email"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30"
              placeholder="maria@cmicmexico.org"
            />
            <p className="text-[10px] text-charcoal-400 mt-1">
              {isNew ? 'Recibirá su usuario y contraseña temporal en este correo.' : 'Cambiar este correo NO envía notificación.'}
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-charcoal-600 mb-2">Rol</label>
            <div className="space-y-2">
              {roleOptions.map((r) => (
                <label
                  key={r.value}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${form.role === r.value ? `border-${r.color}-400 bg-${r.color}-50` : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={r.value}
                    checked={form.role === r.value}
                    onChange={() => setForm({ ...form, role: r.value })}
                    data-testid={`admin-users-role-${r.value}`}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-charcoal-700">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          {form.role !== 'admin' && form.role !== 'council' && (
            <label className="flex items-start gap-2.5 p-3 rounded-lg border border-cmic-200 bg-cmic-50/50 cursor-pointer" data-testid="admin-users-is-council-field">
              <input
                type="checkbox"
                checked={!!form.is_council}
                onChange={(e) => setForm({ ...form, is_council: e.target.checked })}
                data-testid="admin-users-is-council"
                className="mt-0.5 h-4 w-4 accent-cmic-600 cursor-pointer"
              />
              <span className="text-sm text-charcoal-700">
                <strong>Es miembro del consejo directivo</strong>
                <span className="block text-[11px] text-charcoal-500 mt-0.5">
                  Marca esto si esta persona pertenece al consejo (ej. Presidencia). Mostrará la insignia "Consejo" junto a su rol. Déjalo sin marcar para roles de apoyo como "Asistente".
                </span>
              </span>
            </label>
          )}

          {form.role === 'council' && (
            <div data-testid="admin-users-council-title-field">
              <label className="block text-xs font-semibold text-charcoal-600 mb-1">Cargo en el consejo</label>
              <select
                value={form.council_title || ''}
                onChange={(e) => setForm({ ...form, council_title: e.target.value })}
                data-testid="admin-users-council-title"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 bg-white"
              >
                <option value="">— Sin cargo —</option>
                {boardRoles.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
                {form.council_title && !boardRoles.includes(form.council_title) && (
                  <option value={form.council_title}>{form.council_title}</option>
                )}
              </select>
              <p className="text-[10px] text-charcoal-400 mt-1">
                Los cargos provienen de <strong>Consejo Directivo</strong>. Este cargo se mostrará como insignia en el foro.
              </p>
            </div>
          )}

          {isCustomRole && activeCustomRole && (
            <div data-testid="admin-users-custom-role-info" className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-charcoal-600">
                <strong>{activeCustomRole.name}</strong> es un rol personalizado. Sus permisos se definen en la sección <strong>Roles</strong> y se aplican automáticamente. Este usuario no pertenece al consejo directivo.
              </p>
              <div className="mt-2 flex flex-wrap gap-1">
                {(activeCustomRole.permissions || []).length === 0 ? (
                  <span className="text-[11px] text-charcoal-400">Sin permisos definidos.</span>
                ) : (activeCustomRole.permissions || []).map((p) => (
                  <span key={p} className="bg-white border border-slate-200 rounded px-1.5 py-0.5 text-[10px] font-mono text-charcoal-600">{p}</span>
                ))}
              </div>
            </div>
          )}

          {(form.role === 'council' || form.role === 'asistente') && (
            <div data-testid="admin-users-permissions-matrix">
              <label className="block text-xs font-semibold text-charcoal-600 mb-2">Permisos granulares *</label>
              <p className="text-[11px] text-charcoal-500 mb-3">
                Marca <strong>qué puede hacer</strong> el usuario en cada área. Si quieres que solo <em>vea</em>, marca solo &quot;Ver&quot;. Si puede editar pero no eliminar, marca &quot;Ver + Editar&quot;. El admin del sitio (configuración) y la gestión de usuarios se quedan SIEMPRE en super-administrador.
              </p>
              <div className="overflow-x-auto border border-gray-200 rounded-xl">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-charcoal-600 w-[42%]">Área</th>
                      {permActions.map((a) => (
                        <th key={a.key} className="text-center px-2 py-2 font-medium text-charcoal-600 text-[11px]" title={a.description}>
                          {a.label}
                        </th>
                      ))}
                      <th className="text-center px-2 py-2 font-medium text-charcoal-600 text-[11px]">Todo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {permGroups.map((g) => {
                      const groupActions = new Set(g.actions || []);
                      const allChecked = (g.actions || []).every((a) => hasGrant(g.key, a));
                      const anyChecked = (g.actions || []).some((a) => hasGrant(g.key, a));
                      return (
                        <tr key={g.key} className={anyChecked ? 'bg-cmic-50/40' : ''} data-testid={`admin-users-perm-row-${g.key}`}>
                          <td className="px-3 py-2 align-top">
                            <div className="text-sm font-medium text-charcoal-800">{g.label}</div>
                            <div className="text-[10px] text-charcoal-500 mt-0.5">{g.description}</div>
                          </td>
                          {permActions.map((a) => (
                            <td key={a.key} className="text-center px-2 py-2">
                              {groupActions.has(a.key) ? (
                                <input
                                  type="checkbox"
                                  checked={hasGrant(g.key, a.key)}
                                  onChange={() => toggleAction(g.key, a.key)}
                                  data-testid={`admin-users-perm-${g.key}-${a.key}`}
                                  className="h-4 w-4 accent-cmic-600 cursor-pointer"
                                />
                              ) : (
                                <span className="text-gray-300 text-xs select-none">—</span>
                              )}
                            </td>
                          ))}
                          <td className="text-center px-2 py-2">
                            <button
                              type="button"
                              onClick={() => setGroupAllOrNone(g.key, !allChecked)}
                              data-testid={`admin-users-perm-all-${g.key}`}
                              className={`text-[10px] font-semibold px-2 py-1 rounded ${allChecked ? 'bg-cmic-600 text-white' : 'bg-gray-100 text-charcoal-600 hover:bg-gray-200'}`}
                              title={allChecked ? 'Quitar todo' : 'Otorgar todo'}
                            >
                              {allChecked ? 'Quitar' : 'Todo'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] text-charcoal-400 mt-2 flex items-center gap-3">
                <span><strong>Ver:</strong> abre la sección</span>
                <span>·</span>
                <span><strong>Crear:</strong> agrega registros</span>
                <span>·</span>
                <span><strong>Editar:</strong> modifica</span>
                <span>·</span>
                <span><strong>Eliminar:</strong> borra</span>
                <span>·</span>
                <span><strong>Publicar:</strong> envía/aprueba/responde según el área</span>
              </div>
            </div>
          )}

          {form.role !== 'admin' && (
            <div data-testid="admin-users-forum-perms">
              <label className="block text-xs font-semibold text-charcoal-600 mb-2 flex items-center gap-1">
                <Shield size={13} className="text-cmic-600" /> Permisos de moderación del foro
              </label>
              <p className="text-[11px] text-charcoal-500 mb-3">
                Define qué acciones de moderación puede realizar en el foro. Si no marcas ninguna, no podrá moderar.
              </p>
              <div className="space-y-2">
                {FORUM_MOD_OPTIONS.map((opt) => {
                  const checked = (form.forum_mod_permissions || []).includes(opt.key);
                  return (
                    <label key={opt.key} data-testid={`admin-users-forum-perm-${opt.key}`}
                      className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const cur = form.forum_mod_permissions || [];
                          const next = cur.includes(opt.key) ? cur.filter((x) => x !== opt.key) : [...cur, opt.key];
                          setForm({ ...form, forum_mod_permissions: next });
                        }}
                        data-testid={`admin-users-forum-perm-checkbox-${opt.key}`}
                        className="mt-0.5 h-4 w-4 accent-cmic-600 cursor-pointer"
                      />
                      <span>
                        <span className="block text-sm font-medium text-charcoal-800">{opt.label}</span>
                        <span className="block text-[11px] text-charcoal-500">{opt.desc}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {!isNew && (
            <label className="flex items-center gap-2 text-sm text-charcoal-700">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                data-testid="admin-users-active-toggle"
              />
              Usuario activo (desmarca para suspender el acceso sin eliminarlo)
            </label>
          )}

          <label className="flex items-start gap-2.5 p-3 rounded-lg border border-amber-200 bg-amber-50/50 cursor-pointer" data-testid="admin-users-notify-approvals-field">
            <input
              type="checkbox"
              checked={!!form.notify_approvals}
              onChange={(e) => setForm({ ...form, notify_approvals: e.target.checked })}
              data-testid="admin-users-notify-approvals"
              className="mt-0.5 h-4 w-4 accent-cmic-600 cursor-pointer"
            />
            <span className="text-sm text-charcoal-700">
              <strong>Notificar por correo las solicitudes de cambio de perfil</strong>
              <span className="block text-[11px] text-charcoal-500 mt-0.5">
                Si lo desmarcas, este usuario dejará de recibir correos cuando un intérprete solicite cambios en su perfil, pero podrá seguir revisándolos en el panel de <strong>Aprobaciones</strong>.
              </span>
            </span>
          </label>

          {!isNew && (
            <div className="border-t border-gray-200 pt-4" data-testid="admin-users-manual-password">
              <p className="text-xs font-semibold text-charcoal-700 mb-2 uppercase tracking-wider flex items-center gap-1">
                <KeyRound size={13} /> Establecer contraseña manualmente
              </p>
              <p className="text-[11px] text-charcoal-500 mb-3">
                Escribe una contraseña temporal para este usuario (útil si no le llega el correo). Se aplica de inmediato, sin enviar correo.
              </p>
              {pwSet ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="admin-users-manual-pw-result">
                  <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5 mb-2">
                    <CheckCircle2 size={15} /> Contraseña establecida
                  </p>
                  <p className="text-[11px] text-emerald-700 mb-3">Cópiala y compártela con el usuario por un canal seguro. No volverá a mostrarse.</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white border border-emerald-200 rounded-lg px-3 py-2 text-sm font-mono text-charcoal-800 break-all" data-testid="admin-users-manual-pw-value">{pwSet}</code>
                    <button
                      type="button"
                      onClick={copyPassword}
                      data-testid="admin-users-manual-pw-copy"
                      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap ${copied ? 'bg-emerald-600 text-white' : 'bg-cmic-600 hover:bg-cmic-700 text-white'}`}
                    >
                      {copied ? <><Check size={14} /> Copiada</> : <><Copy size={14} /> Copiar</>}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setPwSet(''); setCopied(false); }}
                    data-testid="admin-users-manual-pw-again"
                    className="mt-3 text-xs text-charcoal-500 hover:text-cmic-600 font-medium"
                  >
                    Establecer otra contraseña
                  </button>
                </div>
              ) : (
              <>
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Nueva contraseña (mín. 8)"
                  autoComplete="new-password"
                  data-testid="admin-users-manual-pw"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30"
                />
                <input
                  type="password"
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  placeholder="Confirmar contraseña"
                  autoComplete="new-password"
                  disabled={!pw}
                  data-testid="admin-users-manual-pw-confirm"
                  className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 disabled:bg-gray-50 ${pwMismatch ? 'border-rose-300 bg-rose-50/30' : 'border-gray-200'}`}
                />
              </div>
              <label className={`mt-2.5 flex items-start gap-2 text-xs ${!pw ? 'opacity-50' : ''}`}>
                <input
                  type="checkbox"
                  checked={forcePw}
                  onChange={(e) => setForcePw(e.target.checked)}
                  disabled={!pw}
                  data-testid="admin-users-manual-force"
                  className="mt-0.5 h-4 w-4 accent-cmic-600"
                />
                <span className="text-charcoal-700">
                  <strong>Forzar cambio en el próximo inicio de sesión.</strong>
                  <span className="block text-[11px] text-charcoal-500 mt-0.5">Si lo activas, el usuario deberá definir su propia contraseña al ingresar.</span>
                </span>
              </label>
              {pwError && <p className="text-[11px] text-rose-600 mt-1.5">{pwError}</p>}
              <button
                type="button"
                onClick={handleSetPassword}
                disabled={!canSetPw}
                data-testid="admin-users-manual-pw-submit"
                className="mt-3 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:bg-charcoal-300 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 text-sm font-medium"
              >
                <KeyRound size={14} />{savingPw ? 'Estableciendo…' : 'Establecer contraseña'}
              </button>
              </>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 flex-shrink-0 bg-gray-50">
          <button
            onClick={onClose}
            disabled={saving}
            data-testid="admin-users-modal-cancel"
            className="px-4 py-2 text-sm font-medium text-charcoal-600 hover:text-charcoal-800 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="admin-users-modal-save"
            className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cmic-700 disabled:opacity-50"
          >
            <Save size={14} />{saving ? 'Guardando…' : (isNew ? 'Crear y enviar correo' : 'Guardar cambios')}
          </button>
        </div>
      </div>
    </div>
  );
}
